import React from "react";

import { formatCurrency } from "../../../utils/reportHelpers";
import RevenueTrendChart from "../charts/RevenueTrendChart";
import ChartCard from "../shared/ChartCard";
import ProgressBar from "../shared/ProgressBar";

const MockBadge = () => (
  <span className="text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400 px-2 py-0.5 rounded">
    Sample Data
  </span>
);

const RevenueTab = ({ data, loading, showComparison }) => {
  if (loading) {
    return (
      <div className="space-y-4 sm:space-y-6">
        {/* KPI skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {[1, 2, 3].map((i) => (
            <div
              className="h-28 sm:h-32 rounded-lg animate-pulse bg-gray-200 dark:bg-gray-700"
              key={i}
            />
          ))}
        </div>
        {/* Chart skeleton */}
        <div className="h-[400px] rounded-lg animate-pulse bg-gray-200 dark:bg-gray-700" />
        {/* Products skeleton */}
        <div className="h-[250px] rounded-lg animate-pulse bg-gray-200 dark:bg-gray-700" />
      </div>
    );
  }

  const isMock = data?.revenue?._mock;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* ------------------------------------------------------------------ */}
      {/* 1. KPI Hero Cards                                                  */}
      {/* ------------------------------------------------------------------ */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {/* MRR - green */}
        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-4 sm:p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-green-100">
                Monthly Recurring Revenue
              </p>
              <p className="text-xl sm:text-2xl lg:text-3xl font-bold mt-1 sm:mt-2">
                {formatCurrency(data.revenue.mrr)}
              </p>
              <p className="text-xs sm:text-sm text-green-100 mt-1 sm:mt-2">
                {data.comparison.revenueDelta.value} from last period
              </p>
            </div>
            <i className="fas fa-chart-line text-2xl sm:text-3xl lg:text-4xl text-green-200" />
          </div>
        </div>

        {/* Annual Revenue - blue */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-4 sm:p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-blue-100">Annual Revenue</p>
              <p className="text-xl sm:text-2xl lg:text-3xl font-bold mt-1 sm:mt-2">
                {formatCurrency(data.revenue.arr)}
              </p>
              <p className="text-xs sm:text-sm text-blue-100 mt-1 sm:mt-2">
                {data.targets.mrrProgress}% of yearly target
              </p>
            </div>
            <i className="fas fa-calendar text-2xl sm:text-3xl lg:text-4xl text-blue-200" />
          </div>
        </div>

        {/* AOV - purple */}
        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-4 sm:p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-purple-100">
                Average Order Value
              </p>
              <p className="text-xl sm:text-2xl lg:text-3xl font-bold mt-1 sm:mt-2">
                {formatCurrency(data.revenue.aov)}
              </p>
              <p className="text-xs sm:text-sm text-purple-100 mt-1 sm:mt-2">
                Per transaction
              </p>
            </div>
            <i className="fas fa-shopping-cart text-2xl sm:text-3xl lg:text-4xl text-purple-200" />
          </div>
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* 2. Revenue Breakdown Chart                                         */}
      {/* ------------------------------------------------------------------ */}
      <ChartCard
        title={
          <span className="flex items-center gap-2">
            Revenue Breakdown
            {isMock && <MockBadge />}
          </span>
        }
      >
        <RevenueTrendChart
          categories={data.revenue.categories}
          currentData={data.revenue.chartData}
          height={400}
          previousData={data.revenue.previousPeriodChartData}
          showComparison={showComparison}
          targetLine={data.targets.mrrTarget}
        />
      </ChartCard>

      {/* ------------------------------------------------------------------ */}
      {/* 3. Top Products / Services                                         */}
      {/* ------------------------------------------------------------------ */}
      <ChartCard
        title={
          <span className="flex items-center gap-2">
            Top Products/Services
            {isMock && <MockBadge />}
          </span>
        }
      >
        <div className="space-y-4">
          {data.revenue.topProducts.map((product, index) => (
            <div className="flex items-center justify-between" key={index}>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900 dark:text-white">
                    {product.name}
                  </span>
                  <div className="flex items-center gap-4">
                    <span className="text-gray-600 dark:text-gray-400">
                      {formatCurrency(product.revenue)}
                    </span>
                    <span className="text-green-600 dark:text-green-400 text-sm">
                      {product.growth}
                    </span>
                  </div>
                </div>
                <ProgressBar percentage={product.percentage} />
              </div>
            </div>
          ))}
        </div>
      </ChartCard>
    </div>
  );
};

export default RevenueTab;
